import { releaseKind, type ReleaseKind } from "../musicReleaseTabs";
import { trackIdentityKey } from "../trackIdentity";

// The arcade plays one song per card. Popular songs lead (Spotify's top tracks
// order), then the newest releases, then everything else, and no song shows up
// twice even when it sits on two releases.

export type ArcadeSection = "popular" | "newest" | "more";

export interface ArcadeReleaseInput {
  artists?: Array<{ id?: string; name?: string }>;
  id: string;
  name: string;
  release_date?: string;
  total_tracks?: number;
  album_group?: string;
  images?: Array<{ url: string }>;
}

export interface ArcadeTopTrackInput {
  id: string;
  name: string;
  album?: ArcadeReleaseInput;
}

export interface ArcadeRelease {
  id: string;
  name: string;
  releaseDate: string;
  totalTracks: number;
  kind: ReleaseKind;
  albumGroup?: string;
}

export interface ArcadeSong {
  slug: string;
  title: string;
  trackId?: string;
  trackName?: string;
  release: ArcadeRelease;
  artwork?: string;
  section: ArcadeSection;
  rank: number;
}

export const NEWEST_SECTION_SIZE = 6;
export const HOMEPAGE_ARCADE_PICKS = 4;

export const ARCADE_SECTION_LABELS: Record<ArcadeSection, string> = {
  popular: "Most played",
  newest: "Newest",
  more: "More songs",
};

// Mirrors safeStringWithHyphens in _helpers.tsx so /games/:slug reads the same
// as the /songs/:slug page for that title.
export function arcadeSlug(title: string): string {
  return title.replace(/[^a-z0-9 ]/gi, "").replace(/ /g, "-").toLowerCase();
}

function toRelease(input: ArcadeReleaseInput): ArcadeRelease {
  return {
    id: input.id,
    name: input.name,
    releaseDate: input.release_date || "",
    totalTracks: input.total_tracks || 0,
    kind: releaseKind(input),
    albumGroup: input.album_group,
  };
}

const byNewest = (a: ArcadeReleaseInput, b: ArcadeReleaseInput) => String(b.release_date || "").localeCompare(String(a.release_date || ""));

export function buildArcadeLineup(
  topTracks: ArcadeTopTrackInput[] | null | undefined,
  albums: ArcadeReleaseInput[] | null | undefined,
): ArcadeSong[] {
  const lineup: ArcadeSong[] = [];
  const seenTitles = new Set<string>();
  const seenReleases = new Set<string>();
  const usedSlugs = new Set<string>();
  const ranks: Record<ArcadeSection, number> = { popular: 0, newest: 0, more: 0 };

  const push = (entry: Omit<ArcadeSong, "slug" | "rank">) => {
    const key = trackIdentityKey(entry.title);
    if (!key || seenTitles.has(key)) return;
    const base = arcadeSlug(entry.title) || entry.release.id.toLowerCase();
    let slug = base === "endless" ? "endless-song" : base;
    for (let n = 2; usedSlugs.has(slug); n++) slug = `${base}-${n}`;
    seenTitles.add(key);
    usedSlugs.add(slug);
    seenReleases.add(entry.release.id);
    ranks[entry.section] += 1;
    lineup.push({ ...entry, slug, rank: ranks[entry.section] });
  };

  for (const track of topTracks || []) {
    if (!track?.name || !track.album?.id) continue;
    if (track.album.album_group === "appears_on") continue;
    push({
      title: track.name,
      trackId: track.id,
      trackName: track.name,
      release: toRelease(track.album),
      artwork: track.album.images?.[0]?.url,
      section: "popular",
    });
  }

  const releases = (albums || []).filter((album) => album?.id && album.name && album.album_group !== "appears_on").sort(byNewest);
  for (const album of releases) {
    if (seenReleases.has(album.id)) continue;
    const release = toRelease(album);
    push({
      title: album.name,
      trackName: release.kind === "single" ? album.name : undefined,
      release,
      artwork: album.images?.[0]?.url,
      section: ranks.newest < NEWEST_SECTION_SIZE ? "newest" : "more",
    });
  }

  return lineup;
}

export function lineupBySection(lineup: ArcadeSong[]): Record<ArcadeSection, ArcadeSong[]> {
  return {
    popular: lineup.filter((song) => song.section === "popular"),
    newest: lineup.filter((song) => song.section === "newest"),
    more: lineup.filter((song) => song.section === "more"),
  };
}

// The homepage card leads with popular songs but always keeps a seat for the
// newest release, so a fresh drop is never pushed off by the old favourites.
export function homepageArcadePicks(lineup: ArcadeSong[], count = HOMEPAGE_ARCADE_PICKS): ArcadeSong[] {
  const { popular, newest, more } = lineupBySection(lineup);
  const newestSeats = newest.length > 0 ? 1 : 0;
  const picks = popular.slice(0, Math.max(0, count - newestSeats));
  for (const song of [...newest, ...popular.slice(picks.length), ...more]) {
    if (picks.length >= count) break;
    if (!picks.includes(song)) picks.push(song);
  }
  return picks;
}

export function findArcadeSong(lineup: ArcadeSong[], slug: string | undefined | null): ArcadeSong | null {
  if (!slug) return null;
  const wanted = slug.toLowerCase();
  return lineup.find((song) => song.slug === wanted) ?? null;
}

export function releaseYear(song: ArcadeSong): string {
  return song.release.releaseDate.slice(0, 4);
}

export const ARCADE_ROOT = "/games";

// /games/endless is the endless run, so no song may take that slug.
export const ENDLESS_SLUG = "endless";

// /games, a song's hub (/games/believer, its featured game) or one game for
// the song (/games/believer/beat-lanes). Each is its own indexable page.
export const arcadePath = (slug?: string, kind?: string) => (slug ? `${ARCADE_ROOT}/${slug}${kind ? `/${kind}` : ""}` : ARCADE_ROOT);
